/*
 * Saved Brand Templates — fetches the org's saved brand templates via
 * DeckorateAPI's blob-sas endpoint, same two-step contract as Slide
 * Templates (see slideTemplatesApi.ts): POST blob-sas -> one manifest JSON
 * asset (blob_container/blob_path), GET + parse its `entries` into the
 * template list. Each entry's `pptxFile` resolves to a sibling blob.
 *
 * Manifest entry shape isn't pinned down, so normalisation below accepts
 * several likely field-name spellings and degrades gracefully rather than
 * crashing if the schema shifts.
 */

import { sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { BrandColors } from "../../../types";
import { logError, scrubMessage } from "../../../../../telemetry";

const SAVED_BRAND_TEMPLATES_CONNECTOR = "deckorate";
const BLOB_SAS_PATH = "/v1/deckorate/content/blob-sas";

/* NOTE: client_id is hardcoded for now (same as Slide Templates) — replace
 * with the real Studio client id once available. */
const SAVED_BRAND_TEMPLATES_CLIENT_ID = 1;
const SAVED_BRAND_TEMPLATES_TRANSACTION_TYPE = "TemplateTransaction";
const SAVED_BRAND_TEMPLATES_TEMPLATE_TYPE = "Brand";

const MANIFEST_REQUEST_FAILED_MESSAGE =
  "Couldn't find the saved brand templates library — the blob-sas request didn't return a usable asset.";
const MANIFEST_DOWNLOAD_FAILED_MESSAGE = "Downloading the saved brand templates library failed.";
const MANIFEST_NOT_JSON_MESSAGE = "The saved brand templates response wasn't valid JSON.";

/** Thrown by downloadManifest/fetchSavedBrandTemplateBytes so callers can
 *  branch on the HTTP status (specifically 404) without parsing it back out
 *  of a message string. */
export class BlobFetchHttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "BlobFetchHttpError";
  }
}

export function logSavedBrandTemplatesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "SavedBrandTemplatesError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: SAVED_BRAND_TEMPLATES_CONNECTOR, reason },
  );
}

interface BlobSasRequest {
  client_id: number;
  transaction_type: typeof SAVED_BRAND_TEMPLATES_TRANSACTION_TYPE;
  template_type: typeof SAVED_BRAND_TEMPLATES_TEMPLATE_TYPE;
}

interface RawBlobSasAsset {
  sas_url?: unknown;
  blob_container?: unknown;
  blob_path?: unknown;
}

interface RawBlobSasResponse {
  assets?: unknown;
}

interface ManifestSasAsset {
  readonly sasUrl: string;
  readonly blobPath: string;
  readonly blobContainer: string | null;
}

function firstManifestSasAsset(response: RawBlobSasResponse | null | undefined): ManifestSasAsset | null {
  const assets = response?.assets;
  if (!Array.isArray(assets) || assets.length === 0) return null;
  const asset = assets[0] as RawBlobSasAsset | undefined;
  if (typeof asset?.sas_url !== "string" || !asset.sas_url) return null;
  if (typeof asset.blob_path !== "string" || !asset.blob_path) return null;
  const blobContainer = typeof asset.blob_container === "string" && asset.blob_container ? asset.blob_container : null;
  return { sasUrl: asset.sas_url, blobPath: asset.blob_path, blobContainer };
}

/**
 * Rebuilds a fetchable file URL for `blobPath` against `sasUrl`'s origin +
 * query string. Unlike Slide Templates' SAS (scoped to the manifest's exact
 * folder, so swapping just the last path segment works — see
 * directorySasFileUrl), Brand's SAS can be scoped higher up (e.g. container
 * root); swapping only the last segment there drops subfolders and 404s. So
 * this rebuilds the path from `blob_container` + the FULL `blobPath`
 * instead, falling back to the last-segment swap only if `blob_container`
 * is missing from the response.
 */
function blobFileUrl(
  sasUrl: string,
  blobContainer: string | null,
  blobPath: string,
  onParseError: (message: string) => void,
): string {
  try {
    const url = new URL(sasUrl);
    if (blobContainer) {
      const encodedPath = blobPath.split("/").filter(Boolean).map(encodeURIComponent).join("/");
      url.pathname = `/${encodeURIComponent(blobContainer)}/${encodedPath}`;
      return url.toString();
    }

    const segments = blobPath.split("/");
    let fileName: string | undefined;
    for (let i = segments.length - 1; i >= 0; i--) {
      if (segments[i]) {
        fileName = segments[i];
        break;
      }
    }
    if (!fileName) return sasUrl;
    let pathEnd = url.pathname.length;
    while (pathEnd > 0 && url.pathname[pathEnd - 1] === "/") pathEnd--;
    url.pathname = `${url.pathname.slice(0, pathEnd)}/${encodeURIComponent(fileName)}`;
    return url.toString();
  } catch (error) {
    onParseError((error as TypeError).message);
    return sasUrl;
  }
}

/** Folder portion of a container-relative blob path — "" for a root-level
 *  blob. Used to resolve a sibling `pptxFile` before calling blobFileUrl. */
function blobDirName(blobPath: string): string {
  const idx = blobPath.lastIndexOf("/");
  return idx === -1 ? "" : blobPath.slice(0, idx);
}

export interface SavedBrandTemplate {
  readonly id: string;
  readonly name: string;
  /** "All brands" filter grouping. Null if the entry carries no brand field. */
  readonly brand: string | null;
  /** Row-preview swatches — from `themeColors` when present, else a flat
   *  colours/swatches/palette field. */
  readonly colors: readonly string[] | null;
  /** Full colour scheme from the manifest's `themeColors` object — enough to
   *  build a BrandSpec (buildBrandSpecFromThemeColors) with no .pptx
   *  download. Null falls back to `sourceFileUrlCandidates`. */
  readonly themeColors: Partial<BrandColors> | null;
  /** Best-guess fetchable URL for this entry's .pptx (first of
   *  `sourceFileUrlCandidates`); null when there's no filename to resolve. */
  readonly sourceFileUrl: string | null;
  /** Filename variants worth trying (see pptxFileCandidates) — uploaded
   *  files mix naming conventions, so `pptxFile` doesn't always match the
   *  real blob name. Empty iff `sourceFileUrl` is null. */
  readonly sourceFileUrlCandidates: readonly string[];
  /** Slide aspect ratio (e.g. "16:9", "4:3"), derived from the manifest
   *  entry's `slideDimensionsEmu`. Null when that field is missing — the
   *  manifest carries no font-name data at all, so unlike Format there's no
   *  "Title font"/"Body font" to show. */
  readonly aspectRatio: string | null;
}

const BRAND_COLOR_KEYS = [
  "dk1", "dk2", "lt1", "lt2",
  "accent1", "accent2", "accent3", "accent4", "accent5", "accent6",
  "hlink", "folHlink",
] as const;

/** Parses a manifest entry's `themeColors`/`theme_colors` OBJECT (not the
 *  array shape `colors`/`swatches`/`palette` accept) into a partial
 *  BrandColors, e.g. `{ dk1: "000000", accent1: "2E74B5", ... }`. */
function parseThemeColors(raw: unknown): Partial<BrandColors> | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const result: Partial<Record<(typeof BRAND_COLOR_KEYS)[number], string>> = {};
  for (const key of BRAND_COLOR_KEYS) {
    const value = obj[key];
    if (typeof value === "string" && value.trim()) result[key] = value.replace(/^#/, "");
  }
  return Object.keys(result).length > 0 ? result : null;
}

function themeColorSwatches(themeColors: Partial<BrandColors>): string[] {
  return [
    themeColors.accent1, themeColors.accent2, themeColors.accent3,
    themeColors.accent4, themeColors.accent5, themeColors.accent6,
  ].filter((c): c is string => typeof c === "string" && c.length > 0);
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }
  return null;
}

function firstStringArray(...candidates: unknown[]): readonly string[] | null {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.every((c) => typeof c === "string")) {
      const cleaned = (candidate as string[]).map((c) => c.replace(/^#/, "")).filter(Boolean);
      if (cleaned.length > 0) return cleaned;
    }
  }
  return null;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * Reduces a manifest entry's `slideDimensionsEmu: { cx, cy }` (the slide's
 * width/height in EMUs — PowerPoint's own page-size unit, present regardless
 * of which slide the entry describes) to a simplified ratio label like
 * "16:9" or "4:3". Returns null for missing/non-positive dimensions rather
 * than showing a nonsense ratio.
 */
function parseAspectRatio(raw: unknown): string | null {
  if (typeof raw !== "object" || raw === null) return null;
  const { cx, cy } = raw as { cx?: unknown; cy?: unknown };
  if (typeof cx !== "number" || typeof cy !== "number") return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || cx <= 0 || cy <= 0) return null;
  const divisor = gcd(Math.round(cx), Math.round(cy));
  if (divisor <= 0) return null;
  return `${Math.round(cx / divisor)}:${Math.round(cy / divisor)}`;
}

/** "Northwind_Pitch.pptx" -> "Northwind Pitch". */
function humaniseFileName(raw: string): string {
  return raw
    .replace(/\.pptx$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

interface NormalisedManifestEntry extends SavedBrandTemplate {
  /** Not part of the public shape — used only to dedupe multiple
   *  per-slide entries for the same source file (see normaliseManifest). */
  readonly dedupeKey: string;
}

function normaliseManifestEntry(
  raw: unknown,
  index: number,
  resolveSourceFileUrls: (pptxFile: string) => string[],
): NormalisedManifestEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;

  const pptxFile = firstString(entry.pptxFile, entry.pptx_file, entry.file, entry.fileName, entry.file_name);
  const name =
    firstString(entry.name, entry.templateName, entry.template_name, entry.label, entry.title) ??
    (pptxFile ? humaniseFileName(pptxFile) : null);
  if (!name) return null;

  const id = firstString(entry.id, entry.templateId, entry.template_id, entry.slug) ?? `saved-brand-template-${index}`;
  const brand = firstString(entry.brand, entry.brandName, entry.brand_name, entry.clientName, entry.client_name);
  const themeColors = parseThemeColors(entry.themeColors ?? entry.theme_colors);
  const colors = themeColors ? themeColorSwatches(themeColors) : firstStringArray(entry.colors, entry.swatches, entry.palette);
  const sourceFileUrlCandidates = pptxFile ? resolveSourceFileUrls(pptxFile) : [];
  const sourceFileUrl = sourceFileUrlCandidates[0] ?? null;
  const aspectRatio = parseAspectRatio(entry.slideDimensionsEmu ?? entry.slide_dimensions_emu);

  return { id, name, brand, colors, themeColors, sourceFileUrl, sourceFileUrlCandidates, aspectRatio, dedupeKey: pptxFile ?? id };
}

/**
 * The manifest is a slide-signature index, not one-row-per-template — a
 * saved template's .pptx gets one entry per slide (ids like
 * ".../Brand.pptx#1", "#2"), repeating the same pptxFile/themeColors. Entries
 * sharing a `pptxFile` collapse to the first seen so the tab shows one row
 * per real template.
 */
function normaliseManifest(raw: unknown, resolveSourceFileUrls: (pptxFile: string) => string[]): SavedBrandTemplate[] {
  let items: unknown[] | null = null;
  if (Array.isArray(raw)) {
    items = raw;
  } else if (Array.isArray((raw as { entries?: unknown })?.entries)) {
    items = (raw as { entries: unknown[] }).entries;
  } else if (Array.isArray((raw as { templates?: unknown })?.templates)) {
    items = (raw as { templates: unknown[] }).templates;
  }
  if (!items) {
    logSavedBrandTemplatesError(
      "saved-brand-templates-manifest-shape",
      "Manifest root was neither an array nor { entries: [] } / { templates: [] }",
    );
    return [];
  }

  const seenIds = new Set<string>();
  const uniqueId = (id: string): string => {
    if (!seenIds.has(id)) {
      seenIds.add(id);
      return id;
    }
    let suffix = 2;
    while (seenIds.has(`${id}-${suffix}`)) suffix += 1;
    const deduped = `${id}-${suffix}`;
    seenIds.add(deduped);
    return deduped;
  };

  const seenDedupeKeys = new Set<string>();
  return items
    .map((item, index) => normaliseManifestEntry(item, index, resolveSourceFileUrls))
    .filter((item): item is NormalisedManifestEntry => item !== null)
    .filter((item) => {
      if (seenDedupeKeys.has(item.dedupeKey)) return false;
      seenDedupeKeys.add(item.dedupeKey);
      return true;
    })
    .map(({ dedupeKey: _dedupeKey, ...template }) => ({ ...template, id: uniqueId(template.id) }));
}

async function requestManifestSasAsset(http: HttpClient, options: HttpRequestOptions): Promise<ManifestSasAsset> {
  const request: BlobSasRequest = {
    client_id: SAVED_BRAND_TEMPLATES_CLIENT_ID,
    transaction_type: SAVED_BRAND_TEMPLATES_TRANSACTION_TYPE,
    template_type: SAVED_BRAND_TEMPLATES_TEMPLATE_TYPE,
  };
  const response = await http.post<RawBlobSasResponse | null>(BLOB_SAS_PATH, request, options);
  const asset = firstManifestSasAsset(response);
  if (!asset) throw new Error(MANIFEST_REQUEST_FAILED_MESSAGE);
  return asset;
}

/**
 * KNOWN BACKEND MISMATCH (2026-08-28): blob-sas returns
 * `blob_path: ".../brands-signatures.json"` but the real blob is
 * `brands-signature.json` (singular). Produces the plural/singular filename
 * variant as a fallback, tried only after the exact path 404s. Remove once
 * the backend response matches the real blob name.
 */
function manifestPathCandidates(blobPath: string): string[] {
  const candidates = [blobPath];
  if (/signatures\.json$/i.test(blobPath)) {
    candidates.push(blobPath.replace(/signatures\.json$/i, "signature.json"));
  } else if (/signature\.json$/i.test(blobPath)) {
    candidates.push(blobPath.replace(/signature\.json$/i, "signatures.json"));
  }
  return candidates;
}

/**
 * KNOWN BACKEND INCONSISTENCY: uploaded filenames mix underscore/space
 * conventions (e.g. "WL_Template_Sep2025.pptx" vs "RRD Master 2025.pptx"),
 * and an entry's `pptxFile` doesn't always match the real blob's style.
 * Trying the swapped variant after the manifest's own value 404s covers
 * that — remove once the manifest and blob names agree.
 */
function pptxFileCandidates(pptxFile: string): string[] {
  const candidates = [pptxFile];
  if (pptxFile.includes("_")) candidates.push(pptxFile.replace(/_/g, " "));
  if (pptxFile.includes(" ")) candidates.push(pptxFile.replace(/ /g, "_"));
  return [...new Set(candidates)];
}

async function fetchBlobResponse(url: string, signal: AbortSignal | undefined, reason: string): Promise<Response> {
  const res = await fetch(url, {
    method: "GET",
    headers: sasVersionHeaders(url, logSavedBrandTemplatesError, reason),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      logSavedBrandTemplatesError("saved-brand-templates-blob-body-read", getErrorMessage(error, "Failed to read blob error body"));
      return "";
    });
    throw new BlobFetchHttpError(res.status, `Blob download failed (${res.status} ${errText})`);
  }
  return res;
}

async function downloadManifest(sasUrl: string, signal: AbortSignal | undefined): Promise<unknown> {
  const res = await fetchBlobResponse(sasUrl, signal, "saved-brand-templates-manifest-sas-version-parse");
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    logSavedBrandTemplatesError("saved-brand-templates-manifest-parse", getErrorMessage(error, "Manifest JSON.parse failed"));
    throw new Error(MANIFEST_NOT_JSON_MESSAGE);
  }
}

/**
 * Fetches a saved template's raw .pptx bytes, trying each `candidateUrls`
 * entry in order and only continuing on a 404 (any other failure surfaces
 * immediately). Shared by useBrandExtractor and useSavedBrandTemplateSwatches
 * so the fallback logic lives in one place.
 */
export async function fetchSavedBrandTemplateBytes(candidateUrls: readonly string[], signal?: AbortSignal): Promise<ArrayBuffer> {
  let lastError: unknown;
  for (const url of candidateUrls) {
    try {
      const res = await fetchBlobResponse(url, signal, "saved-brand-templates-source-sas-version-parse");
      return await res.arrayBuffer();
    } catch (error) {
      lastError = error;
      if (!(error instanceof BlobFetchHttpError) || error.status !== 404) throw error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Saved template download failed — no source file found.");
}

/** Fetch + parse the saved brand templates manifest, normalised into a lenient, UI-ready shape. */
export async function fetchSavedBrandTemplates(http: HttpClient, options: HttpRequestOptions = {}): Promise<SavedBrandTemplate[]> {
  const asset = await requestManifestSasAsset(http, options);
  const candidates = manifestPathCandidates(asset.blobPath);

  let manifest: unknown;
  let lastError: unknown;
  let resolvedBlobPath = asset.blobPath;
  for (const candidatePath of candidates) {
    const manifestFileUrl = blobFileUrl(asset.sasUrl, asset.blobContainer, candidatePath, (message) =>
      logSavedBrandTemplatesError("saved-brand-templates-sas-rebuild", message),
    );
    try {
      manifest = await downloadManifest(manifestFileUrl, options.signal);
      resolvedBlobPath = candidatePath;
      if (candidatePath !== asset.blobPath) {
        logSavedBrandTemplatesError(
          "saved-brand-templates-manifest-filename-mismatch",
          `blob-sas returned "${asset.blobPath}" but only "${candidatePath}" exists — using the fallback filename.`,
        );
      }
      break;
    } catch (error) {
      lastError = error;
      // Only a "not found at this exact path" error is worth trying another
      // filename for — any other failure (auth, network, malformed JSON)
      // would fail identically on every candidate, so surface it immediately.
      if (!(error instanceof BlobFetchHttpError) || error.status !== 404) throw error;
    }
  }
  if (manifest === undefined) throw lastError instanceof Error ? lastError : new Error(MANIFEST_DOWNLOAD_FAILED_MESSAGE);

  const manifestDir = blobDirName(resolvedBlobPath);
  const resolveSourceFileUrls = (pptxFile: string): string[] =>
    pptxFileCandidates(pptxFile).map((candidateFile) => {
      const siblingBlobPath = manifestDir ? `${manifestDir}/${candidateFile}` : candidateFile;
      return blobFileUrl(asset.sasUrl, asset.blobContainer, siblingBlobPath, (message) =>
        logSavedBrandTemplatesError("saved-brand-templates-source-url-rebuild", message),
      );
    });
  return normaliseManifest(manifest, resolveSourceFileUrls);
}
